import apiClient, { lambdaClient, publicClient } from 'src/services/ApiService';

import { track } from 'src/utils/analytics';

// `details`: etichette dei lookup (sede, centro di costo, responsabile, CCNL...) e valori dei
// campi personalizzati. Serve solo alla tabella Anagrafiche; altrove si evita il payload extra.
// `scope: 'expenses'`: dipendenti dell'ambito note spese del responsabile (es. selettore della nuova nota).
const getEmployees = (company_id, { details = false, scope } = {}) =>
  apiClient.get(`/company/${company_id}/employees`, {
    params: { ...(details ? { details: 'true' } : {}), ...(scope ? { scope } : {}) },
  });

const getOrgChart = (company_id) =>
  apiClient.get(`/company/${company_id}/org-chart`);

const getMyTeamEmployees = (company_id) =>
  apiClient.get(`/company/${company_id}/employees/my-team`);

const getEmployeesSecurityReport = (company_id) =>
  apiClient.get(`/company/${company_id}/employees/security-report`);

const getEmployeesSecurityReportExcel = (company_id) =>
  apiClient.get(`/company/${company_id}/employees/security-report?excel=true`)
    .then((res) => { track('Report Generato', { company_id, report_type: 'security', format: 'xlsx' }); return res; });

// Query per il Report Residui Dipendenti: periodo di riferimento + mese di confronto (opz.,
// storico o futuro/proiezione — deciso lato backend in base alla disponibilità del cedolino).
const buildPayrollDataQuery = ({ year, month, compareYear, compareMonth } = {}) => {
  const params = new URLSearchParams();
  if (year && month) { params.set('year', year); params.set('month', month); }
  if (compareYear && compareMonth) { params.set('compareYear', compareYear); params.set('compareMonth', compareMonth); }
  return params.toString();
};

const getEmployeesPayrollDataReport = (company_id, opts = {}) => {
  const qs = buildPayrollDataQuery(opts);
  return apiClient.get(`/company/${company_id}/employees/payroll-data${qs ? `?${qs}` : ''}`);
};

const getEmployeesPayrollDataReportExcel = (company_id, opts = {}) => {
  const qs = buildPayrollDataQuery(opts);
  return apiClient.get(`/company/${company_id}/employees/payroll-data?excel=true${qs ? `&${qs}` : ''}`)
    .then((res) => { track('Report Generato', { company_id, report_type: 'payroll_data', format: 'xlsx' }); return res; });
}

const getEmployeesCustomReportFields = (company_id) =>
  apiClient.get(`/company/${company_id}/employees/custom-report/fields`);

const generateEmployeesCustomReport = (company_id, data) =>
  apiClient.post(`/company/${company_id}/employees/custom-report`, data)
    .then((res) => { track('Report Generato', { company_id, report_type: 'custom' }); return res; });

const exportEmployeesCustomReportXLS = (company_id, data) =>
  apiClient.post(`/company/${company_id}/employees/custom-report/export`, data)
    .then((res) => { track('Report Generato', { company_id, report_type: 'custom', format: 'xlsx' }); return res; });

const getEmployeesCustomReportPresets = (company_id) =>
  apiClient.get(`/company/${company_id}/employees/custom-report/presets`);

const saveEmployeesCustomReportPreset = (company_id, data) =>
  apiClient.post(`/company/${company_id}/employees/custom-report/presets`, data);

const deleteEmployeesCustomReportPreset = (company_id, preset_id) =>
  apiClient.delete(`/company/${company_id}/employees/custom-report/presets/${preset_id}`);

// `columns`: chiavi delle colonne visibili in tabella, nell'ordine della tabella (null = tutte).
const exportEmployeesXLS = (company_id, ids = null, timeline = false, columns = null) => {
  const params = new URLSearchParams({ timeline: String(timeline) });
  if (ids) params.set('ids', ids.join(','));
  if (columns?.length) params.set('columns', columns.join(','));
  return apiClient.get(`/company/${company_id}/employees/export?${params.toString()}`)
    .then((res) => { track('Dipendenti Esportati', { company_id, format: 'xlsx' }); return res; });
};

// Anagrafiche dipendenti alla data X: numero in forza + scarico XLSX. `date` = 'YYYY-MM-DD'.
const getEmployeesHeadcount = (company_id, date = null) =>
  apiClient.get(`/company/${company_id}/employees/headcount${date ? `?date=${date}` : ''}`);

const exportEmployeesSnapshotXLS = (company_id, date = null, onlyInForce = false, ids = null, columns = null) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (onlyInForce) params.set('only_in_force', 'true');
  if (ids) params.set('ids', ids.join(','));
  if (columns?.length) params.set('columns', columns.join(','));
  const qs = params.toString();
  return apiClient.get(`/company/${company_id}/employees/snapshot/export${qs ? `?${qs}` : ''}`)
    .then((res) => { track('Fotografia Dipendenti Esportata', { company_id, format: 'xlsx' }); return res; });
};

const getEmployee = (company_id, employee_id) =>
  apiClient.get(`/company/${company_id}/employees/${employee_id}`);

const updateEmployee = (company_id, employee_id, data) =>
  apiClient.put(`/company/${company_id}/employees/${employee_id}`, data)
    .then((res) => { track('Dipendente Modificato', { company_id, section: 'anagrafica' }); return res; });

const disableEmployeeMfa = (company_id, employee_id) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/disable-mfa`);

const removeEmployeeSuppression = (company_id, employee_id) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/remove-email-suppression`);

const deleteEmployee = (company_id, employee_id) =>
  apiClient.delete(`/company/${company_id}/employees/${employee_id}`);

const inviteEmployee = (company_id, employee_id) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/invite`);

const updateEmployeeCareer = (company_id, employee_id, data) =>
  apiClient.put(`/company/${company_id}/employees/${employee_id}/career`, data)
    .then((res) => { track('Dipendente Modificato', { company_id, section: 'career' }); return res; });

const getInailRates = (company_id) => apiClient.get(`/company/${company_id}/inail-rates`);

const getDashboardPreferences = (companyId) => apiClient.get(`/company/${companyId}/dashboard/preferences`);
const saveDashboardPreferences = (companyId, config) => apiClient.put(`/company/${companyId}/dashboard/preferences`, config);
const getDashboardSummary = (companyId, kind) => apiClient.get(`/company/${companyId}/dashboard/summary/${kind}`);
const getDashboardReportChartFields = (companyId, presetId) => apiClient.get(`/company/${companyId}/dashboard/report-chart-fields/${presetId}`);
const getDashboardReportChart = (companyId, data) => apiClient.post(`/company/${companyId}/dashboard/report-chart`, data);

const getCompany = (company_id) => apiClient.get(`/company/${company_id}`);
const getReference = (catalog) => apiClient.get(`/reference/${catalog}`);

const updateCompany = (company_id, data) => apiClient.put(`/company/${company_id}`, data)
  .then((res) => { track('Profilo Azienda Salvato', { company_id }); return res; });

const getDocuments = (company_id, path = null) =>
  apiClient.get(`/company/${company_id}/documents`, { params: { path } });

const getSignatureDocuments = (company_id) =>
  apiClient.get(`/company/${company_id}/documents/signatures`);

const getDocumentOverview = (company_id) =>
  apiClient.get(`/company/${company_id}/documents/overview`);

const deleteDocumentOverview = (company_id, rowId, confirmation) =>
  apiClient.delete(`/company/${company_id}/documents/overview/${encodeURIComponent(rowId)}`, { data: { confirmation } });

const previewDocumentOverview = (company_id, rowId) =>
  apiClient.get(`/company/${company_id}/documents/overview/${encodeURIComponent(rowId)}/preview`, { responseType: 'blob' });

const deleteSignatureDocument = (company_id, document_id, confirmation) =>
  apiClient.delete(`/company/${company_id}/documents/signatures/${document_id}`, { data: { confirmation } });

const initCompanySignatureSession = (company_id, documentIds) =>
  apiClient.post(`/company/${company_id}/documents/signatures/init`, { documentIds });

const executeCompanySignature = (company_id, sessionId, otp) =>
  apiClient.post(`/company/${company_id}/documents/signatures/${sessionId}/sign`, { otp })
    .then((res) => { track('Documento Firmato', { company_id }); return res; });

const getClockings = (company_id, from, to) =>
  apiClient.get(`/company/${company_id}/clockings?from=${from}&to=${to}`);

const createFolderDocument = (company_id, data) =>
  apiClient.post(`/company/${company_id}/documents/folder`, data);

const createDocument = (company_id, path) =>
  apiClient.post(`/company/${company_id}/documents`, { key: path });

const notifyDocument = (company_id, keys_list) =>
  apiClient.post(`/company/${company_id}/documents-notify`, { keys_list });

const downloadFolderZip = (company_id, key) =>
  apiClient.get(`/company/${company_id}/documents/download-zip?key=${key}`);

const downloadDocument = (company_id, key) =>
  apiClient.get(`/company/${company_id}/documents/download?key=${key}`);

const deleteDocument = (company_id, key) =>
  apiClient.delete(`/company/${company_id}/documents?key=${key}`);

const moveDocument = (company_id, key, new_key) =>
  apiClient.put(`/company/${company_id}/documents/move?key=${encodeURIComponent(key)}&new_key=${encodeURIComponent(new_key)}`);

const moveFolder = (company_id, source_key, destination_path) =>
  apiClient.put(`/company/${company_id}/documents/move-folder?source_key=${encodeURIComponent(source_key)}&destination_path=${encodeURIComponent(destination_path)}`);

const createEmployee = (company_id, data) =>
  apiClient.postForm(`/company/${company_id}/employees`, data)
    .then((res) => { track('Dipendente Creato', { company_id, source: 'manual' }); return res; });

const getImportEmployees = (company_id) =>
  apiClient.get(`/company/${company_id}/employee/import`);

// File ricevuti dal connettore timbrature (monitoraggio).
const getClockingImports = (company_id) =>
  apiClient.get(`/company/${company_id}/clocking-imports`);

const getEmployeeBadges = (company_id, employee_id) =>
  apiClient.get(`/company/${company_id}/employees/${employee_id}/badges`);

const addEmployeeBadge = (company_id, employee_id, badge_code) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/badges`, { badge_code });

const removeEmployeeBadge = (company_id, employee_id, badge_id) =>
  apiClient.delete(`/company/${company_id}/employees/${employee_id}/badges/${badge_id}`);

const getUnknownBadges = (company_id) =>
  apiClient.get(`/company/${company_id}/clocking-badges/unknown`);

const associateBadge = (company_id, badge_code, employee_id) =>
  apiClient.post(`/company/${company_id}/clocking-badges/associate`, { badge_code, employee_id });

const importEmployees = (company_id, data) =>
  apiClient.post(`/company/${company_id}/employee/import`, data)
    .then((res) => { track('Dipendenti Importati', { company_id }); return res; });

const importTimeline = (company_id, data) =>
  apiClient.post(`/company/${company_id}/employee/timeline/import`, data);

const getCompanyTimesheet = (company_id, year, month) =>
  apiClient.get(`/company/${company_id}/timesheets/${year}/${month}`);

const getCompanyTimesheetAttachments = (company_id, year, month) =>
  apiClient.get(`/company/${company_id}/timesheets/${year}/${month}/attachments`);

const updateCompanyTimesheet = (company_id, year, month, data) =>
  apiClient.post(`/company/${company_id}/timesheets/${year}/${month}`, data);

const uploadCompanyTimesheetAttachment = (company_id, year, month, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post(`/company/${company_id}/timesheets/${year}/${month}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const getTimesheets = (company_id, year = null, month = null) =>
  year != null && month != null
    ? apiClient.get(`/company/${company_id}/timesheets?year=${year}&month=${month}`) : apiClient.get(`/company/${company_id}/timesheets`);

const closeTimesheets = (company_id, timesheet_ids) =>
  apiClient.post(`/company/${company_id}/timesheets/close`, { timesheet_ids })
    .then((res) => { track('Timesheet Chiuso', { company_id, count: timesheet_ids?.length }); return res; });

const checkTimesheet = (company_id, year, month, status) =>
  apiClient.get(
    `/company/${company_id}/timesheets/check?year=${year}&month=${month}&status=${status}`
  );

const exportCheckTimesheetXLS = (company_id, year, month, status) =>
  apiClient.get(
    `/company/${company_id}/timesheets/check?year=${year}&month=${month}&status=${status}&excel=true`
  );

// statuses: stati separati da virgola (es. '3,4'); vuoto = tutti.
const getTimesheetCheckReport = (company_id, year, month, statuses, excel = false) =>
  apiClient.get(`/company/${company_id}/report/timesheet-check`, {
    params: { year, month, ...(statuses && { status: statuses }), ...(excel && { excel: true }) },
  });

const confirmTimesheet = (company_id, year, month) =>
  apiClient.get(
    `/company/${company_id}/timesheets/confirm?year=${year}&month=${month}`
  );

const sendTimesheetToPayroll = (company_id, year, month) =>
  apiClient.get(
    `/company/${company_id}/timesheets/send-to-payroll?year=${year}&month=${month}`
  );

const exportPDFTimesheet = (company_id, year, month) =>
  apiClient.get(
    `/company/${company_id}/timesheets/pdf?year=${year}&month=${month}`
  );

const updateEmployeeTimesheet = (company_id, employee_id, timesheet) =>
  apiClient.put(
    `/company/${company_id}/employees/${employee_id}/timesheet`,
    timesheet
  ).then((res) => { track('Timesheet Modificato', { company_id, category: timesheet?.category }); return res; });

const getEmployeeTimesheet = (company_id, employee_id, year, month) =>
  apiClient.get(
    `/company/${company_id}/employees/${employee_id}/timesheet?year=${year}&month=${month}`
  );

const exportEmployeeTimesheetXLS = (company_id, employee_id, year, month) => apiClient.get(
  `/company/${company_id}/employees/${employee_id}/timesheet/export?year=${year}&month=${month}`
);

const exportTimesheetsXLS = (company_id, year, month, ids = null) => apiClient.get(
  ids ? `/company/${company_id}/timesheet/export?year=${year}&month=${month}&ids=${ids.join(',')}` : `/company/${company_id}/employees/export?year=${year}&month=${month}`
).then((res) => { track('Report Generato', { company_id, report_type: 'timesheet', format: 'xlsx' }); return res; });

const importTimesheetLUL = (company_id, data) =>
  apiClient.postForm(`/company/${company_id}/timesheets/import-lul`, data)
    .then((res) => { track('LUL Importato', { company_id }); return res; });

// Passaggi dal tornello del giorno (sola lettura).
const getEmployeeClockingPunches = (company_id, employee_id, date) =>
  apiClient.get(`/company/${company_id}/employees/${employee_id}/clocking-punches?date=${date}`);

const getEmployeeClockings = (company_id, employee_id, date) =>
  apiClient.get(
    `/company/${company_id}/employees/${employee_id}/clockings?date=${date}`
  );

const getEmployeeTimeline = (company_id, employee_id) =>
  apiClient.get(`/company/${company_id}/employees/${employee_id}/timeline`);

const getEmployeeRevisions = (company_id, employee_id, { cursor, limit } = {}) =>
  apiClient.get(`/company/${company_id}/employees/${employee_id}/revisions`, { params: { cursor, limit } });

const createEmployeeCustomField = (company_id, data) => apiClient.post(`/company/${company_id}/custom-fields`, data);

const updateEmployeeCustomField = (company_id, field_id, data) => apiClient.put(`/company/${company_id}/custom-fields/${field_id}`, data);

const deleteEmployeeCustomField = (company_id, field_id) => apiClient.delete(`/company/${company_id}/custom-fields/${field_id}`);

const createEmployeeStatusOption = (company_id, data) => apiClient.post(`/company/${company_id}/status-options`, data);

const updateEmployeeStatusOption = (company_id, option_id, data) => apiClient.put(`/company/${company_id}/status-options/${option_id}`, data);

const deleteEmployeeStatusOption = (company_id, option_id) => apiClient.delete(`/company/${company_id}/status-options/${option_id}`);

const applyDefaultStatusOptions = (company_id) => apiClient.post(`/company/${company_id}/status-options/apply-defaults`);

const getEmployeeCustomFields = (company_id) =>
  apiClient.get(`/company/${company_id}/custom-fields`);

const getEmployeeStatusOptions = (company_id) =>
  apiClient.get(`/company/${company_id}/status-options`);

const getEmployeeCustomFieldValues = (company_id, employee_id) =>
  apiClient.get(`/company/${company_id}/employees/${employee_id}/custom-fields`);

const updateEmployeeCustomFieldValues = (company_id, employee_id, values) =>
  apiClient.put(`/company/${company_id}/employees/${employee_id}/custom-fields`, { values });

const addEmployeeTimeline = (company_id, employee_id, value, valid_from) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/timeline`, {
    value,
    valid_from
  });

const updateEmployeeTimeline = (company_id, employee_id, timeline_id, data) =>
  apiClient.put(
    `/company/${company_id}/employees/${employee_id}/timeline/${timeline_id}`,
    data
  );

const deleteEmployeeTimeline = (company_id, employee_id, timeline_id) =>
  apiClient.delete(
    `/company/${company_id}/employees/${employee_id}/timeline/${timeline_id}`
  );

const deleteEmployeeClocking = (clocking_id) =>
  apiClient.delete(`clocking/${clocking_id}`);

const addClocking = (data) => apiClient.post(`clocking/manual`, data);

const qrCodeClocking = (data) => publicClient.post(`clocking/qr-code`, data);

const checkDeviceCode = (data) => publicClient.post(`clocking/check-device`, data);

const updateClocking = (clocking_id, data) =>
  apiClient.put(`clocking/${clocking_id}`, data);

const addTimesheetRequest = (company_id, data) =>
  apiClient.post(`/company/${company_id}/timesheets/requests`, data);

const selfApproveTimesheetRequest = (company_id, request_id) =>
  apiClient.post(`/company/${company_id}/timesheets/requests/${request_id}/self-approve`, {});

const uploadTimesheetRequestAttachment = (company_id, request_id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post(`/company/${company_id}/timesheets/requests/${request_id}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const getTimesheetRequests = (company_id, params) =>
  apiClient.get(`/company/${company_id}/timesheets/requests`, { params });

const getTimesheetRequest = (company_id, request_id, token = null) =>
  apiClient.get(`/company/${company_id}/timesheets/requests/${request_id}?token=${token}`);

const approveTimesheetRequest = (company_id, request_id, request) =>
  apiClient.post(
    `/company/${company_id}/timesheets/requests/${request_id}/approve`,
    request
  ).then((res) => { track('Richiesta Gestita', { company_id, decision: 'approved' }); return res; });

const rejectTimesheetRequest = (company_id, request_id, request) =>
  apiClient.post(
    `/company/${company_id}/timesheets/requests/${request_id}/reject`,
    request
  ).then((res) => { track('Richiesta Gestita', { company_id, decision: 'rejected' }); return res; });

const cancelTimesheetRequest = async (company_id, request_id, replaced_by = null) =>
  apiClient.post(`company/${company_id}/timesheets/requests/${request_id}/cancel${replaced_by ? `?replaced_by=${replaced_by}` : ''}`, {})

const dismissCancelTimesheetRequest = async (company_id, request_id) =>
  apiClient.post(`company/${company_id}/timesheets/requests/${request_id}/dismiss-cancel`, {})

const exportTimesheetRequestsXLS = async (company_id) => apiClient.get(`company/${company_id}/timesheets/requests/export`);

const getOffices = (company_id) =>
  apiClient.get(`/company/${company_id}/offices`);

const updateOffice = (company_id, office_id, data) =>
  apiClient.put(`/company/${company_id}/offices/${office_id}`, data);

const createOffice = (company_id, data) =>
  apiClient.post(`/company/${company_id}/offices`, data);

const deleteOffice = (company_id, office_id) =>
  apiClient.delete(`/company/${company_id}/offices/${office_id}`);

const getTimeProfiles = (company_id) =>
  apiClient.get(`/company/${company_id}/time-profiles`);

const getTimesheetCategories = (company_id) =>
  apiClient.get(`/company/${company_id}/timesheet-categories`);

const updateTimesheetCategoryCompany = (company_id, short_code, data) =>
  apiClient.put(`/company/${company_id}/timesheet-categories/${short_code}`, data);

const getRequestsCategories = (company_id) => apiClient.get(`/company/${company_id}/requests-categories`)

const getCompanyRequestsCategories = (company_id) => apiClient.get(`/company/${company_id}/company-requests-categories`)

const updateTimesheet = (data) => apiClient.put(`/timesheet`, data);

const updateMonthlyTimesheet = (data) => apiClient.put(`/timesheet/monthly`, data);

const rangeUpdateTimesheet = (data) =>
  apiClient.put(`/timesheet/range-justify`, data);

const getAnomalies = (company_id) =>
  apiClient.get(`/company/${company_id}/anomalies`);

const getEmployeeDailyAnomalies = (company_id, employee_id, date) =>
  apiClient.get(
    `/company/${company_id}/anomalies?date=${date}&employee_id=${employee_id}`
  );

const getEmployeeMonthAnomalies = (company_id, employee_id, year, month) =>
  apiClient.get(`/company/${company_id}/anomalies?employee_id=${employee_id}&year=${year}&month=${month}`);

const verifyAnomaly = (company_id, employee_id, year, month, day, anomaly_code) =>
  apiClient.post(`/company/${company_id}/anomalies/verify`, { employee_id, year, month, day, anomaly_code });

const deleteAnomaly = (
  company_id,
  employee_id,
  year,
  month,
  day,
  anomaly_code
) =>
  apiClient.post(`/company/${company_id}/anomalies/delete`, {
    employee_id,
    year,
    month,
    day,
    anomaly_code
  }).then((res) => { track('Anomalia Risolta', { company_id, anomaly_code }); return res; });

const getDailyAnomalies = (company_id, date) =>
  apiClient.get(`/company/${company_id}/anomalies?date=${date}`);

const computeEmployeeTimesheet = (employee_id, date) =>
  apiClient.post(
    `/timesheet/compute-day?date=${date}&employee_id=${employee_id}`
  );

const computeEmployeeMonthTimesheet = (employee_id, date) =>
  apiClient.post(
    `/timesheet/compute-month?date=${date}&employee_id=${employee_id}`
  );

const createEmployeeTimesheet = (employee_id, date) =>
  lambdaClient.get(
    `/createEmployeeTimesheet?date=${date}&employee_id=${employee_id}`
  );

const getExpenseReportCategories = (company_id) =>
  apiClient.get(`/company/${company_id}/config/expense-report`);

const getExpenseReportCustomFields = (company_id, includeInactive = false) => apiClient.get(`/company/${company_id}/config/expense-report/custom-fields`, { params: { include_inactive: includeInactive } });
const createExpenseReportCustomField = (company_id, data) => apiClient.post(`/company/${company_id}/config/expense-report/custom-fields`, data);
const updateExpenseReportCustomField = (company_id, field_id, data) => apiClient.put(`/company/${company_id}/config/expense-report/custom-fields/${field_id}`, data);
const archiveExpenseReportCustomField = (company_id, field_id) => apiClient.delete(`/company/${company_id}/config/expense-report/custom-fields/${field_id}`);

const createExpenseReportCategory = (company_id, data) =>
  apiClient.post(`/company/${company_id}/config/expense-report`, data);

const updateExpenseReportCategory = (company_id, category_id, data) =>
  apiClient.put(`/company/${company_id}/config/expense-report/${category_id}`, data);

const deleteExpenseReportCategory = (company_id, category_id) =>
  apiClient.delete(`/company/${company_id}/config/expense-report/${category_id}`);

const createExpenseReport = (company_id, data) =>
  apiClient.post(`/company/${company_id}/expense-report`, data);

const uploadExpenseReportAttachment = (company_id, report_id, file, kind = 'receipt') => {
  const formData = new FormData();
  formData.append('kind', kind);
  formData.append('file', file);
  return apiClient.post(`/company/${company_id}/expense-report/${report_id}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const getExpenseReports = (company_id) =>
  apiClient.get(`/company/${company_id}/expense-report`);

const exportExpenseReportsXLS = (company_id, ids = null) => apiClient.get(
  ids ? `/company/${company_id}/expense-report/export?ids=${ids.join(',')}` : `/company/${company_id}/expense-report/export`
);

const getExpenseReportAttachments = (company_id, expense_report_id) =>
  apiClient.get(
    `/company/${company_id}/expense-report/${expense_report_id}/attachments`
  );

const updateExpenseReport = (company_id, data) =>
  apiClient.put(`/company/${company_id}/expense-report/${data.id}`, data);

// Respingi al dipendente per correzione: la motivazione è obbligatoria lato API
const returnExpenseReport = (company_id, report, review_notes) =>
  apiClient.put(`/company/${company_id}/expense-report/${report.id}`, {
    id: report.id,
    employee_id: report.employee_id,
    status: 'returned',
    review_notes,
  });

const sendExpenseReportsToPayroll = (company_id, data) =>
  apiClient.post(`/company/${company_id}/expense-report/send-to-payroll`, data);

// Raggruppa note spese esistenti (stesso dipendente, non già in trasferta) in una trasferta
const groupExpenseReportsIntoTrip = (company_id, data) =>
  apiClient.post(`/company/${company_id}/expense-report/group-trip`, data);

// Approvazione multipla: nessun endpoint bulk lato API, si riusa l'update singolo
// (che applica già controllo richiedente≠approvatore e invio email per riga).
// `reports` = [{ id, employee_id }]. Ritorna il conteggio approvate/fallite.
const approveExpenseReports = async (company_id, reports) => {
  const results = await Promise.allSettled(
    reports.map((r) =>
      updateExpenseReport(company_id, {
        id: r.id,
        employee_id: r.employee_id,
        status: 'approved',
      })
    )
  );
  return {
    approved: results.filter((x) => x.status === 'fulfilled').length,
    failed: results.filter((x) => x.status === 'rejected').length,
  };
};

const addHelpTicket = (company_id, data) =>
  apiClient.post(`/company/${company_id}/help-desk`, data);

const uploadHelpDeskAttachment = (company_id, ticket_id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post(`/company/${company_id}/help-desk/${ticket_id}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const getHelpTickets = (company_id) =>
  apiClient.get(`/company/${company_id}/help-desk`);

const getHelpTicket = (company_id, ticket_id) =>
  apiClient.get(`/company/${company_id}/help-desk/${ticket_id}`);

const getTools = (company_id) => apiClient.get(`/company/${company_id}/tools`);

const getDevices = (company_id) => apiClient.get(`/company/${company_id}/devices`);

const createDevice = (company_id, data) => apiClient.post(`/company/${company_id}/devices`, data);

const deleteDevice = (company_id, device_code) => apiClient.delete(`/company/${company_id}/devices/${device_code}`);

const createTool = (company_id, data) =>
  apiClient.post(`/company/${company_id}/tools`, data);

const deleteTool = (company_id, tool_id) => apiClient.delete(`/company/${company_id}/tools/${tool_id}`);

const getCostCenters = (company_id) =>
  apiClient.get(`/company/${company_id}/cost-centers`);

const createCostCenter = (company_id, data) =>
  apiClient.post(`/company/${company_id}/cost-centers`, data);

const getAreas = (company_id) => apiClient.get(`/company/${company_id}/areas`);

const deleteCostCenter = (company_id, cost_center_code) =>
  apiClient.delete(`/company/${company_id}/cost-centers/${cost_center_code}`);

const getEmployeeTools = (company_id, employee_id) =>
  apiClient.get(`/company/${company_id}/employees/${employee_id}/tools`);

const deleteEmployeeTool = (company_id, employee_id, tool_id) =>
  apiClient.delete(
    `/company/${company_id}/employees/${employee_id}/tools/${tool_id}`
  );

const createEmployeeTool = (company_id, employee_id, data) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/tools`, data);

const updateEmployeeTool = (company_id, employee_id, tool_id, data) =>
  apiClient.put(
    `/company/${company_id}/employees/${employee_id}/tools/${tool_id}`,
    data
  );

const requestEmployeeTermination = (company_id, employee_id, data) =>
  apiClient.put(
    `/company/${company_id}/employees/${employee_id}/termination`,
    data
  );

const getEmployeeRequests = (company_id, employee_id) =>
  apiClient.get(
    `/company/${company_id}/employees/${employee_id}/timesheets/requests`
  );

const getEmployeeRequestsByDate = (company_id, employee_id, date) =>
  apiClient.get(
    `/company/${company_id}/employees/${employee_id}/timesheets/requests?date=${date}`
  );

const searchDocuments = (companyId, params) => apiClient.get(`/company/${companyId}/documents/search`, { params });
const searchEmployeeDocuments = (companyId, employeeId, params) =>
  apiClient.get(`/company/${companyId}/employees/${employeeId}/documents/search`, { params });

const getEmployeeDocuments = (company_id, employee_id, path) => apiClient.get(
  `/company/${company_id}/employees/${employee_id}/documents`, { params: { path } }
);

const createEmployeeFolders = (company_id, employee_id) =>
  apiClient.post(
    `/company/${company_id}/employees/${employee_id}/documents/default-folders`
  );

const createEmployeeFolder = (company_id, employee_id, data) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/documents/folder`, data);

const createEmployeeDocument = (company_id, employee_id, key) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/documents`, {
    key
  });

const processSignatureDocument = (company_id, employee_id, key, signatureFields = null, signatureOrder = null) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/documents/process-signature`, {
    key,
    ...(signatureFields && { signatureFields }),
    ...(signatureOrder && { signatureOrder })
  });

const notifyEmployeeDocument = (company_id, employee_id, keys_list, needs_signature) =>
  apiClient.post(`/company/${company_id}/employees/${employee_id}/documents-notify`, {
    keys_list,
    needs_signature
  });

const downloadEmployeeDocument = (company_id, employee_id, key) =>
  apiClient.get(
    `/company/${company_id}/employees/${employee_id}/documents/download?key=${key}`
  );

const deleteEmployeeDocument = (company_id, employee_id, key) =>
  apiClient.delete(
    `/company/${company_id}/employees/${employee_id}/documents?key=${key}`
  );

const getEmployeeCalendarEvents = (company_id, employee_id) =>
  apiClient.get(
    `/company/${company_id}/employees/${employee_id}/calendar-events`
  );

const createCalendarEvent = (company_id, data) =>
  apiClient.post(`/company/${company_id}/calendar-events`, data)
    .then((res) => { track('Scadenza Creata', { company_id }); return res; });

const getCalendarEvents = (company_id) =>
  apiClient.get(`/company/${company_id}/calendar-events`);

const deleteCalendarEvent = (company_id, event_id) =>
  apiClient.delete(`/company/${company_id}/calendar-events/${event_id}`);

const getImports = (company_id, matricola = null, category, date) =>
  apiClient.get(
    matricola ? `/company/${company_id}/imports?matricola=${matricola}&category=${category}&date=${date}`
      : `/company/${company_id}/imports?category=${category}&date=${date}`
  );

const getImportTypes = (company_id) =>
  apiClient.get(`/company/${company_id}/import-types`);

const getLaborCost = (company_id) =>
  apiClient.get(`/company/${company_id}/labor-cost`);

const simulateLaborCost = (company_id, data) =>
  apiClient.post(`/company/${company_id}/labor-cost/simulate`, data)
    .then((res) => { track('Costo Lavoro Simulato', { company_id }); return res; });

const exportLaborCost = (company_id, data) =>
  apiClient.post(`/company/${company_id}/labor-cost/simulate/export`, data);

const exportImportData = (company_id, category, start, end) =>
  apiClient.get(`/company/${company_id}/imports/export?category=${category}&start=${start}&end=${end}`);

const getImportAvailableMonths = (company_id, category) =>
  apiClient.get(`/company/${company_id}/imports/available-months?category=${category}`);

const updateTableTimesheet = (employee_id, rows, year, month) =>
  apiClient.put(`/timesheet/table`, {
    employee_id,
    rows,
    year,
    month
  }).then((res) => { track('Timesheet Modifica Multipla', { employee_id }); return res; });

const createTimesheet = (company_id, employee_id) => apiClient.post(`/timesheet/create-timesheet`, {
  company_id,
  employee_id
});

const getExtraFields = (company_id) =>
  apiClient.get(`/company/${company_id}/timesheet-extra`);

const getEmployeeExtraFields = (company_id, employee_id, year, month) =>
  apiClient.get(
    `/company/${company_id}/timesheet-extra/${employee_id}/${year}/${month}`
  );

const updateExtraFields = (company_id, employee_id, year, month, data) =>
  apiClient.put(
    `/company/${company_id}/timesheet-extra/${employee_id}/${year}/${month}`,
    data
  );

const checkEmployeeData = (company_id, data, update = false) =>
  apiClient.post(update ? `/company/${company_id}/checkEmployee?update=true` : `/company/${company_id}/checkEmployee`, data);

const getTimesheetStats = (company_id, from_year, from_month, to_year, to_month) =>
  apiClient.get(`/company/${company_id}/timesheet/stats/${from_year}/${from_month}/${to_year}/${to_month}`);

const exportStatsXLS = (company_id, from_year, from_month, to_year, to_month) =>
  apiClient.get(`/company/${company_id}/timesheet/stats/${from_year}/${from_month}/${to_year}/${to_month}?excel=true`)
    .then((res) => { track('Report Generato', { company_id, report_type: 'timesheet_stats', format: 'xlsx' }); return res; });

const getLetters = (company_id) => apiClient.get(`/company/${company_id}/letters`);

const getLetter = (company_id, letter_id) => apiClient.get(`/company/${company_id}/letters/${letter_id}`);

const getLetterAttachments = (company_id, letter_id) => apiClient.get(`/company/${company_id}/letters/${letter_id}/attachments`);

const getSupervisors = (company_id) => apiClient.get(`/company/${company_id}/supervisors`);

const addSupervisor = (company_id, data) => apiClient.post(`/company/${company_id}/supervisors`, data);

const deleteSupervisor = (company_id, supervisor_id) => apiClient.delete(`/company/${company_id}/supervisors/${supervisor_id}`);

const getManagers = (company_id, params) => apiClient.get(`/company/${company_id}/managers`, { params });

const updateSupervisor = (company_id, supervisor_id, data) => apiClient.put(`/company/${company_id}/supervisors/${supervisor_id}`, data);

const addManager = (company_id, data) => apiClient.post(`/company/${company_id}/managers`, data);

const removeManager = (company_id, email) => apiClient.delete(`/company/${company_id}/managers/${email}`);

const getTimesheetAttachments = (employee_id, date) => apiClient.get(`/timesheet/attachments?date=${date}&employee_id=${employee_id}`);

const getEmployeeDayProjects = (employee_id, date) => apiClient.get(`/timesheet/projects?date=${date}&employee_id=${employee_id}`);

const requestReopenTimesheet = (company_id, year, month) => apiClient.post(`/company/${company_id}/timesheets/request-reopen`, {
  year,
  month
});

const reopenTimesheet = (company_id, timesheet_id) => apiClient.post(`/company/${company_id}/timesheets/${timesheet_id}/reopen`);

const getPopups = (company_id) => apiClient.get(`/company/${company_id}/popups`);

const getFileLogs = (company_id) => apiClient.get(`/company/${company_id}/file-access-logs`);

const getFileLogsExcel = (company_id) => apiClient.get(`/company/${company_id}/file-access-logs?excel=true`)
  .then((res) => { track('Report Generato', { company_id, report_type: 'access', format: 'xlsx' }); return res; });

const getSubscription = (company_id) => apiClient.get(`/company/${company_id}/subscription`);

const getSubscriptionInvoices = (company_id) => apiClient.get(`/company/${company_id}/invoices`);

const getSubscriptionInvoiceAttachment = (company_id, invoice_id) => apiClient.get(`/company/${company_id}/invoices/${invoice_id}/attachments`);

const getHolidayPlan = (company_id, excel = false) =>
  apiClient.get(`/company/${company_id}/holiday-plan${excel ? '?excel=true' : ''}`);

const getHolidayPlanningData = (company_id, viewMode, year, month, area = null) => {
  const params = new URLSearchParams({ viewMode, year: year.toString(), companyId: company_id.toString() });
  if (month) {
    params.append('month', month.toString());
  }
  if (area) {
    params.append('area', area);
  }
  return apiClient.get(`/timesheet/holiday-planning?${params.toString()}`);
};

const createHolidayPlanObjective = (company_id, data) =>
  apiClient.post(`/company/${company_id}/holiday-plan/objectives`, data);

const getHolidayPlanObjectives = (company_id) =>
  apiClient.get(`/company/${company_id}/holiday-plan/objectives`);

const updateHolidayPlanObjective = (company_id, objective_id, data) =>
  apiClient.put(`/company/${company_id}/holiday-plan/objectives/${objective_id}`, data);

const deleteHolidayPlanObjective = (company_id, objective_id) =>
  apiClient.delete(`/company/${company_id}/holiday-plan/objectives/${objective_id}`);

const getHolidayPlanTimesheetHours = (company_id, year, categories = ['holiday', 'leave', 'ex_festivity']) =>
  apiClient.get(`/company/${company_id}/holiday-plan/timesheet-hours?year=${year}&categories=${categories.join(',')}`);

const getHolidayPlanMetrics = (company_id, year) =>
  apiClient.get(`/company/${company_id}/holiday-plan/metrics?year=${year}`);

const getHolidayPlanRules = (company_id) =>
  apiClient.get(`/company/${company_id}/holiday-plan/rules`);

const createHolidayPlanRule = (company_id, data) =>
  apiClient.post(`/company/${company_id}/holiday-plan/rules`, data);

const updateHolidayPlanRule = (company_id, rule_id, data) =>
  apiClient.put(`/company/${company_id}/holiday-plan/rules/${rule_id}`, data);

const deleteHolidayPlanRule = (company_id, rule_id) =>
  apiClient.delete(`/company/${company_id}/holiday-plan/rules/${rule_id}`);

const generateHolidayPlanObjectives = (company_id, year, dryRun = false) =>
  apiClient.post(`/company/${company_id}/holiday-plan/objectives/generate?year=${year}&dryRun=${dryRun}`);

// Document preparation and delivery.
const compositionForm = (file, input) => {
  const form = new FormData(); form.append('file', file);
  if (input) form.append('input', JSON.stringify(input));
  return form;
};
const analyzeDocumentComposition = (companyId, file, signal) =>
  apiClient.post(`/company/${companyId}/document-composition/analyze`, compositionForm(file), { signal });
const listCompositionRecipients = (companyId) =>
  apiClient.get(`/company/${companyId}/document-composition/recipients`);
const previewDocumentComposition = (companyId, file, input, signal) =>
  apiClient.post(`/company/${companyId}/document-composition/preview`, compositionForm(file, input), { signal, responseType: 'blob' });

const analyzeCompositionAttachment = (companyId, file, kind, signal) => {
  const form = compositionForm(file);
  form.append('kind', kind);
  return apiClient.post(`/company/${companyId}/document-composition/attachments/analyze`, form, { signal });
};
const previewCompositionPdf = (companyId, file, input, attachments, signal) => {
  const form = compositionForm(file, input);
  attachments.common.forEach((attachment) => form.append('common', attachment.file));
  if (attachments.individual) form.append('individual', attachments.individual.file);
  return apiClient.post(`/company/${companyId}/document-composition/preview-pdf`, form, { signal, responseType: 'blob' });
};
const createCompositionDelivery = (companyId, file, input, attachments, signal) => {
  const form = compositionForm(file, input);
  attachments.common.forEach((attachment) => form.append('common', attachment.file));
  if (attachments.individual) form.append('individual', attachments.individual.file);
  return apiClient.post(`/company/${companyId}/document-composition/deliveries`, form, { signal });
};

// Document templates
const getDocumentTemplates = (company_id, category) =>
  apiClient.get(`/company/${company_id}/document-templates${category ? `?category=${category}` : ''}`);

const getDocumentTemplateFields = (company_id) => apiClient.get(`/company/${company_id}/document-templates/fields`);

const createCustomTemplate = (company_id, formData) =>
  apiClient.post(`/company/${company_id}/document-templates`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => { track('Modello Documento Creato', { company_id }); return res; });

const updateCustomTemplate = (company_id, template_id, formData) =>
  apiClient.put(`/company/${company_id}/document-templates/${template_id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => { track('Modello Documento Aggiornato', { company_id }); return res; });

const deleteCustomTemplate = (company_id, template_id) =>
  apiClient.delete(`/company/${company_id}/document-templates/${template_id}`);

const generateDocument = (company_id, template_id_or_key, employee_id, companyInput) =>
  apiClient.post(`/company/${company_id}/document-templates/${template_id_or_key}/generate`, {
    employeeId: employee_id,
    ...(companyInput ? { companyInput } : {}),
  }).then((res) => { track('Documento Generato', { company_id }); return res; });

const createDocumentGenerationRun = (company_id, templateIdOrKey, recipients) =>
  apiClient.post(`/company/${company_id}/document-templates/runs`, { templateIdOrKey, recipients });
const listDocumentGenerationRuns = (company_id) =>
  apiClient.get(`/company/${company_id}/document-templates/runs`);
const getDocumentGenerationRun = (company_id, runId) =>
  apiClient.get(`/company/${company_id}/document-templates/runs/${runId}`);
const generateDocumentRunItem = (company_id, runId, itemId) =>
  apiClient.post(`/company/${company_id}/document-templates/runs/${runId}/items/${itemId}/generate`);

const previewSystemTemplate = (company_id, template_key) =>
  apiClient.get(`/company/${company_id}/document-templates/${template_key}/preview`, { responseType: 'blob' });

const previewCustomTemplate = (company_id, template_id) =>
  apiClient.get(`/company/${company_id}/document-templates/${template_id}/preview`, { responseType: 'blob' });

const downloadCustomTemplate = (company_id, template_id) =>
  apiClient.get(`/company/${company_id}/document-templates/${template_id}/download`, { responseType: 'blob' });

const getEmployeeGeneratedDocuments = (company_id, employee_id) =>
  apiClient.get(`/company/${company_id}/employee-documents${employee_id ? `?employeeId=${employee_id}` : ''}`);

const deleteEmployeeGeneratedDocument = (company_id, doc_id, confirmation) =>
  apiClient.delete(`/company/${company_id}/employee-documents/${doc_id}`, { data: { confirmation } });

const getEmployeeGeneratedDocumentUrl = (company_id, doc_id) =>
  apiClient.get(`/company/${company_id}/employee-documents/${doc_id}/url`);

const publishEmployeeGeneratedDocument = (company_id, doc_id, targetFolder) =>
  apiClient.post(`/company/${company_id}/employee-documents/${doc_id}/publish`, { targetFolder });

const previewEmployeeGeneratedDocument = (company_id, doc_id) =>
  apiClient.get(`/company/${company_id}/employee-documents/${doc_id}/preview`, { responseType: 'blob' });

const uploadImportPayslipPdf = (company_id, data) =>
  apiClient.post(`/company/${company_id}/import-payslip-pdf`, data);

const processUploadedPayslipPdf = (company_id, data) =>
  apiClient.post(`/company/${company_id}/import-payslip-pdf/process`, data)
    .then((res) => { track('Buste Paga Caricate', { company_id }); return res; });

const removePayslipPreviewPage = (company_id, data) =>
  apiClient.delete(`/company/${company_id}/import-payslip-pdf/preview/page`, { data });

const getProjects = (company_id) => apiClient.get(`/company/${company_id}/projects`);

const createProject = (company_id, data) => apiClient.post(`/company/${company_id}/projects`, data);

const updateProject = (company_id, project_id, data) => apiClient.put(`/company/${company_id}/projects/${project_id}`, data);

const deleteProject = (company_id, project_id) => apiClient.delete(`/company/${company_id}/projects/${project_id}`);

const getProjectsMonitoring = (company_id, from, to) =>
  apiClient.get(`/company/${company_id}/projects/monitoring`, { params: { from, to } });

const exportProjectsMonitoring = (company_id, from, to) =>
  apiClient.get(`/company/${company_id}/projects/monitoring/export`, { params: { from, to } });

// Trasparenza retributiva (pratiche)
const getPayTransparency = (company_id) => apiClient.get(`/company/${company_id}/pay-transparency`);
const getPayTransparencyItem = (company_id, id) => apiClient.get(`/company/${company_id}/pay-transparency/${id}`);
const createPayTransparency = (company_id, data) => apiClient.post(`/company/${company_id}/pay-transparency`, data);
const updatePayTransparency = (company_id, id, data) => apiClient.put(`/company/${company_id}/pay-transparency/${id}`, data);
const cancelPayTransparency = (company_id, id) => apiClient.post(`/company/${company_id}/pay-transparency/${id}/cancel`);
const getPayTransparencyAttachments = (company_id, id) =>
  apiClient.get(`/company/${company_id}/pay-transparency/${id}/attachments`);
const uploadPayTransparencyAttachment = (company_id, id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post(`/company/${company_id}/pay-transparency/${id}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
const deletePayTransparencyAttachment = (company_id, id, key) =>
  apiClient.delete(`/company/${company_id}/pay-transparency/${id}/attachment`, { params: { key } });

// Trasparenza retributiva (gruppi omogenei + assegnazione storicizzata)
const getPayTransparencyGroups = (company_id) => apiClient.get(`/company/${company_id}/pay-transparency/groups`);
const createPayTransparencyGroup = (company_id, data) =>
  apiClient.post(`/company/${company_id}/pay-transparency/groups`, data);
const updatePayTransparencyGroup = (company_id, groupId, data) =>
  apiClient.put(`/company/${company_id}/pay-transparency/groups/${groupId}`, data);
const deactivatePayTransparencyGroup = (company_id, groupId) =>
  apiClient.delete(`/company/${company_id}/pay-transparency/groups/${groupId}`);
const getEmployeeHomogeneousGroup = (company_id, employeeId) =>
  apiClient.get(`/company/${company_id}/pay-transparency/employee/${employeeId}/group`);
const assignEmployeeHomogeneousGroup = (company_id, employeeId, group_id, valid_from) =>
  apiClient.post(`/company/${company_id}/pay-transparency/employee/${employeeId}/group`, { group_id, valid_from });
const removeEmployeeHomogeneousGroup = (company_id, employeeId, timelineId) =>
  apiClient.delete(`/company/${company_id}/pay-transparency/employee/${employeeId}/group/${timelineId}`);
// Gruppi assegnabili cross-azienda: propri + delle aziende collegate (master), con azienda proprietaria.
const getAssignablePayTransparencyGroups = (company_id) =>
  apiClient.get(`/company/${company_id}/pay-transparency/assignable-groups`);

// Trasparenza retributiva (elaborazione pratica)
const computePayTransparency = (company_id, reqId) =>
  apiClient.post(`/company/${company_id}/pay-transparency/${reqId}/compute`);
const getPayTransparencyComputation = (company_id, reqId) =>
  apiClient.get(`/company/${company_id}/pay-transparency/${reqId}/computation`);
const setPayTransparencyMember = (company_id, reqId, memberId, body) =>
  apiClient.put(`/company/${company_id}/pay-transparency/${reqId}/computation/member/${memberId}`, body);
const addPayTransparencyMember = (company_id, reqId, body) =>
  apiClient.post(`/company/${company_id}/pay-transparency/${reqId}/computation/member`, body);
const removePayTransparencyMember = (company_id, reqId, memberId) =>
  apiClient.delete(`/company/${company_id}/pay-transparency/${reqId}/computation/member/${memberId}`);
const recalculatePayTransparency = (company_id, reqId) =>
  apiClient.post(`/company/${company_id}/pay-transparency/${reqId}/computation/recalculate`);
// Candidati cross-azienda per l'aggiunta manuale di un membro: dipendenti del cluster (azienda + collegate).
const getPayTransparencyCandidates = (company_id) =>
  apiClient.get(`/company/${company_id}/pay-transparency/candidates`);
const generatePayTransparencyDocuments = (company_id, reqId) =>
  apiClient.post(`/company/${company_id}/pay-transparency/${reqId}/documents/generate`);
const getPayTransparencyDocuments = (company_id, reqId) =>
  apiClient.get(`/company/${company_id}/pay-transparency/${reqId}/documents`);
const sendPayTransparencyDocuments = (company_id, reqId) =>
  apiClient.post(`/company/${company_id}/pay-transparency/${reqId}/documents/send`);
const getPayTransparencyAnalysis = (company_id, date) =>
  apiClient.get(`/company/${company_id}/pay-transparency/analysis`, { params: date ? { date } : {} });
const simulatePayTransparencyAnalysis = (company_id, data) =>
  apiClient.post(`/company/${company_id}/pay-transparency/analysis`, data);
const applyPayTransparencyAnalysisChanges = (company_id, data) =>
  apiClient.post(`/company/${company_id}/pay-transparency/analysis/apply`, data);
const getPayTransparencyBase = (company_id) => apiClient.get(`/company/${company_id}/pay-transparency/base`);
const updatePayTransparencyBase = (company_id, data) => apiClient.put(`/company/${company_id}/pay-transparency/base`, data);

// HeyJob Connect: API in sola lettura per integrazioni esterne
const getConnectOpenApi = (company_id) => apiClient.get(`/company/${company_id}/connect/openapi.json`);
const getConnectKeys = (company_id) => apiClient.get(`/company/${company_id}/connect/keys`);
const getConnectUsage = (company_id) => apiClient.get(`/company/${company_id}/connect/usage`);

const getDashboardHr = companyId => apiClient.get(`/company/${companyId}/dashboard/hr`);

const CompanyService = {
  getReference,
  getDashboardHr,
  getDashboardPreferences,
  saveDashboardPreferences,
  getDashboardSummary,
  getDashboardReportChartFields,
  getDashboardReportChart,
  createTimesheet,
  getEmployees,
  getOrgChart,
  getMyTeamEmployees,
  getEmployeesSecurityReport,
  getEmployeesSecurityReportExcel,
  getEmployeesPayrollDataReport,
  getEmployeesPayrollDataReportExcel,
  getEmployeesCustomReportFields,
  generateEmployeesCustomReport,
  exportEmployeesCustomReportXLS,
  getEmployeesCustomReportPresets,
  saveEmployeesCustomReportPreset,
  deleteEmployeesCustomReportPreset,
  exportEmployeesXLS,
  getEmployeesHeadcount,
  exportEmployeesSnapshotXLS,
  inviteEmployee,
  getCompany,
  updateCompany,
  getDocuments,
  getClockings,
  createFolderDocument,
  createDocument,
  notifyDocument,
  downloadFolderZip,
  downloadDocument,
  deleteDocument,
  moveDocument,
  moveFolder,
  createEmployee,
  getImportEmployees,
  getClockingImports,
  getEmployeeBadges,
  addEmployeeBadge,
  removeEmployeeBadge,
  getUnknownBadges,
  associateBadge,
  getCompanyTimesheet,
  getCompanyTimesheetAttachments,
  updateCompanyTimesheet,
  uploadCompanyTimesheetAttachment,
  importEmployees,
  importTimeline,
  getTimesheets,
  closeTimesheets,
  checkTimesheet,
  exportCheckTimesheetXLS,
  confirmTimesheet,
  sendTimesheetToPayroll,
  exportPDFTimesheet,
  updateEmployeeTimesheet,
  addTimesheetRequest,
  selfApproveTimesheetRequest,
  uploadTimesheetRequestAttachment,
  getTimesheetRequests,
  getTimesheetRequest,
  approveTimesheetRequest,
  rejectTimesheetRequest,
  cancelTimesheetRequest,
  dismissCancelTimesheetRequest,
  exportTimesheetRequestsXLS,
  getEmployeeTimesheet,
  exportEmployeeTimesheetXLS,
  exportTimesheetsXLS,
  getTimesheetCheckReport,
  importTimesheetLUL,
  getEmployeeClockings,
  getEmployeeClockingPunches,
  getEmployeeTimeline,
  getEmployeeRevisions,
  getEmployeeCustomFields,
  createEmployeeCustomField,
  updateEmployeeCustomField,
  deleteEmployeeCustomField,
  createEmployeeStatusOption,
  updateEmployeeStatusOption,
  deleteEmployeeStatusOption,
  applyDefaultStatusOptions,
  getEmployeeStatusOptions,
  getEmployeeCustomFieldValues,
  updateEmployeeCustomFieldValues,
  addEmployeeTimeline,
  updateEmployeeTimeline,
  deleteEmployeeTimeline,
  deleteEmployeeClocking,
  addClocking,
  qrCodeClocking,
  checkDeviceCode,
  updateClocking,
  getEmployee,
  updateEmployee,
  disableEmployeeMfa,
  removeEmployeeSuppression,
  deleteEmployee,
  updateEmployeeCareer,
  getInailRates,
  getOffices,
  createOffice,
  updateOffice,
  deleteOffice,
  getTimeProfiles,
  getTimesheetCategories,
  updateTimesheetCategoryCompany,
  getRequestsCategories,
  getCompanyRequestsCategories,
  updateTimesheet,
  updateMonthlyTimesheet,
  rangeUpdateTimesheet,
  getAnomalies,
  getEmployeeDailyAnomalies,
  getEmployeeMonthAnomalies,
  verifyAnomaly,
  getDailyAnomalies,
  deleteAnomaly,
  computeEmployeeTimesheet,
  computeEmployeeMonthTimesheet,
  createEmployeeTimesheet,
  getExpenseReportCategories,
  getExpenseReportCustomFields,
  createExpenseReportCustomField,
  updateExpenseReportCustomField,
  archiveExpenseReportCustomField,
  createExpenseReportCategory,
  updateExpenseReportCategory,
  deleteExpenseReportCategory,
  createExpenseReport,
  uploadExpenseReportAttachment,
  getExpenseReports,
  exportExpenseReportsXLS,
  updateExpenseReport,
  getExpenseReportAttachments,
  sendExpenseReportsToPayroll,
  groupExpenseReportsIntoTrip,
  approveExpenseReports,
  returnExpenseReport,
  addHelpTicket,
  uploadHelpDeskAttachment,
  getHelpTickets,
  getHelpTicket,
  getTools,
  getDevices,
  createDevice,
  deleteDevice,
  createTool,
  deleteTool,
  getCostCenters,
  getAreas,
  createCostCenter,
  deleteCostCenter,
  createEmployeeTool,
  updateEmployeeTool,
  getEmployeeTools,
  deleteEmployeeTool,
  requestEmployeeTermination,
  getEmployeeRequests,
  getEmployeeRequestsByDate,
  getEmployeeDocuments,
  searchDocuments,
  searchEmployeeDocuments,
  createEmployeeFolders,
  createEmployeeFolder,
  createEmployeeDocument,
  processSignatureDocument,
  notifyEmployeeDocument,
  getSignatureDocuments,
  getDocumentOverview,
  deleteDocumentOverview,
  previewDocumentOverview,
  deleteSignatureDocument,
  initCompanySignatureSession,
  executeCompanySignature,
  downloadEmployeeDocument,
  deleteEmployeeDocument,
  getEmployeeCalendarEvents,
  createCalendarEvent,
  getCalendarEvents,
  deleteCalendarEvent,
  getImports,
  getImportTypes,
  getLaborCost,
  simulateLaborCost,
  exportLaborCost,
  exportImportData,
  getImportAvailableMonths,
  updateTableTimesheet,
  getExtraFields,
  getEmployeeExtraFields,
  updateExtraFields,
  checkEmployeeData,
  getTimesheetStats,
  exportStatsXLS,
  getLetters,
  getLetter,
  getLetterAttachments,
  getSupervisors,
  updateSupervisor,
  addSupervisor,
  deleteSupervisor,
  getManagers,
  addManager,
  removeManager,
  getTimesheetAttachments,
  getEmployeeDayProjects,
  requestReopenTimesheet,
  reopenTimesheet,
  getPopups,
  getFileLogs,
  getFileLogsExcel,
  getSubscription,
  getSubscriptionInvoices,
  getSubscriptionInvoiceAttachment,
  getHolidayPlan,
  getHolidayPlanningData,
  getHolidayPlanTimesheetHours,
  getHolidayPlanMetrics,
  createHolidayPlanObjective,
  getHolidayPlanObjectives,
  updateHolidayPlanObjective,
  deleteHolidayPlanObjective,
  getHolidayPlanRules,
  createHolidayPlanRule,
  updateHolidayPlanRule,
  deleteHolidayPlanRule,
  generateHolidayPlanObjectives,
  analyzeDocumentComposition,
  listCompositionRecipients,
  previewDocumentComposition,
  analyzeCompositionAttachment,
  previewCompositionPdf,
  createCompositionDelivery,
  getDocumentTemplates,
  getDocumentTemplateFields,
  createCustomTemplate,
  updateCustomTemplate,
  deleteCustomTemplate,
  generateDocument,
  createDocumentGenerationRun,
  listDocumentGenerationRuns,
  getDocumentGenerationRun,
  generateDocumentRunItem,
  previewSystemTemplate,
  previewCustomTemplate,
  downloadCustomTemplate,
  getEmployeeGeneratedDocuments,
  deleteEmployeeGeneratedDocument,
  getEmployeeGeneratedDocumentUrl,
  publishEmployeeGeneratedDocument,
  previewEmployeeGeneratedDocument,
  uploadImportPayslipPdf,
  processUploadedPayslipPdf,
  removePayslipPreviewPage,
  getProjects,
  createProject,
  updateProject,
  deleteProject,
  getProjectsMonitoring,
  exportProjectsMonitoring,
  getPayTransparency,
  getPayTransparencyItem,
  createPayTransparency,
  updatePayTransparency,
  cancelPayTransparency,
  getPayTransparencyAttachments,
  uploadPayTransparencyAttachment,
  deletePayTransparencyAttachment,
  getPayTransparencyGroups,
  createPayTransparencyGroup,
  updatePayTransparencyGroup,
  deactivatePayTransparencyGroup,
  getEmployeeHomogeneousGroup,
  assignEmployeeHomogeneousGroup,
  removeEmployeeHomogeneousGroup,
  getAssignablePayTransparencyGroups,
  computePayTransparency,
  getPayTransparencyComputation,
  setPayTransparencyMember,
  addPayTransparencyMember,
  removePayTransparencyMember,
  recalculatePayTransparency,
  getPayTransparencyCandidates,
  generatePayTransparencyDocuments,
  getPayTransparencyDocuments,
  sendPayTransparencyDocuments,
  getPayTransparencyAnalysis,
  simulatePayTransparencyAnalysis,
  applyPayTransparencyAnalysisChanges,
  getPayTransparencyBase,
  updatePayTransparencyBase,
  getConnectOpenApi,
  getConnectKeys,
  getConnectUsage,
};
export default CompanyService;
